/**
 * Shared rules for the customer-facing payment document (the downloadable PDF
 * and the public receipt page), so both always agree on what the document is
 * called, which number it carries and how its values read.
 */
import { Customer, Vehicle } from "@/types";
import { PaymentOutput } from "@/types/payment.types";
import { shortIdRaw } from "@/lib/shortId";

export const COMPANY_INFO = {
  name: "WeClean Car Wash",
  tagline: "We come to you",
  phone: "+974 7090 8373",
  website: "www.theweclean.com",
  email: "info@weclean.com.qa",
};

export const DOCUMENT_FOOTER_LINES = [
  "Thank you for choosing WeClean.",
  "We come to you.",
];

export type PaymentDocumentType = "Invoice" | "Receipt";

/**
 * A payment is only a Receipt once it is fully settled. Anything still open
 * (unpaid or partially paid) is an Invoice asking for the balance.
 */
export function getPaymentDocumentType(payment: PaymentOutput): PaymentDocumentType {
  if (payment.status === "COMPLETED") {
    return "Receipt";
  }
  return "Invoice";
}

/**
 * The document number. Invoices and receipts run separate sequences for
 * accounting ("INV-10001", "REC-10001"). Falls back to the booking reference,
 * then the short payment id, only for a payment that has no number yet.
 */
export function getPaymentDocumentNumber(payment: PaymentOutput): string {
  const documentNumber =
    getPaymentDocumentType(payment) === "Receipt"
      ? payment.receiptNumber
      : payment.invoiceNumber;
  if (documentNumber?.trim()) {
    return documentNumber.trim();
  }

  const bookingRef = payment.bookingRef?.trim();
  if (bookingRef) {
    return bookingRef;
  }
  return shortIdRaw(payment.id);
}

export function getPaymentCustomerName(
  payment: PaymentOutput,
  customer?: Customer | null,
): string {
  return customer?.name?.trim() || payment.customer?.name?.trim() || "";
}

export interface BillToDetails {
  name: string;
  /** Phone, email and "address, city" -- only the ones the customer has. */
  contactLines: string[];
}

/**
 * Who the document is billed to. Returns null when the customer is unknown,
 * so the document can leave the block out rather than print empty labels.
 */
export function getBillToDetails(
  payment: PaymentOutput,
  customer?: Customer | null,
): BillToDetails | null {
  const name = getPaymentCustomerName(payment, customer);
  if (!name) {
    return null;
  }

  const phone = customer?.phone;
  const email = customer?.email;
  const address = customer?.address;
  const city = customer?.city;

  const contactLines: string[] = [];
  if (isMeaningfulPart(phone)) {
    contactLines.push(phone.trim());
  }
  if (isMeaningfulPart(email)) {
    contactLines.push(email.trim());
  }

  const addressParts: string[] = [];
  if (isMeaningfulPart(address)) {
    addressParts.push(address.trim());
  }
  if (isMeaningfulPart(city)) {
    addressParts.push(city.trim());
  }
  if (addressParts.length > 0) {
    contactLines.push(addressParts.join(", "));
  }

  return { name, contactLines };
}

/** Characters Windows and macOS refuse in a filename. */
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]+/g;

/**
 * "<Customer Name> - <Document Number>.pdf", falling back to
 * "<Invoice|Receipt> - <Document Number>.pdf" when the customer is unknown.
 */
export function getPaymentDocumentFileName(
  payment: PaymentOutput,
  customer?: Customer | null,
): string {
  const customerName = getPaymentCustomerName(payment, customer);
  const prefix = customerName || getPaymentDocumentType(payment);
  const baseName = `${prefix} - ${getPaymentDocumentNumber(payment)}`
    .replace(INVALID_FILENAME_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim();
  return `${baseName}.pdf`;
}

/** Customer-friendly payment status. */
export function formatPaymentStatusLabel(payment: PaymentOutput): string {
  if (payment.status === "COMPLETED") {
    return "Paid";
  }
  if (payment.status === "PENDING") {
    if (payment.paidAmount > 0) {
      return "Partially Paid";
    }
    return "Unpaid";
  }
  return toTitleCase(payment.status);
}

/** "BANK_TRANSFER" -> "Bank Transfer". */
export function formatPaymentMode(mode: string | null | undefined): string {
  if (!mode) {
    return "-";
  }
  return toTitleCase(mode);
}

function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function isMeaningfulPart(value: string | null | undefined): value is string {
  if (!value) {
    return false;
  }
  const trimmed = value.trim();
  return trimmed !== "" && trimmed.toUpperCase() !== "N/A";
}

/**
 * "Audi Q8 (2333)". Skips a missing/"N/A" model and does not repeat the
 * name when the model equals the make ("Defender Defender").
 */
export function formatVehicleLabel(vehicle: Pick<Vehicle, "make" | "model" | "plateNumber">): string {
  const nameParts: string[] = [];
  if (isMeaningfulPart(vehicle.make)) {
    nameParts.push(vehicle.make.trim());
  }
  if (
    isMeaningfulPart(vehicle.model) &&
    vehicle.model.trim().toLowerCase() !== vehicle.make?.trim().toLowerCase()
  ) {
    nameParts.push(vehicle.model.trim());
  }

  const name = nameParts.join(" ") || "Vehicle";
  if (isMeaningfulPart(vehicle.plateNumber)) {
    return `${name} (${vehicle.plateNumber.trim()})`;
  }
  return name;
}

export interface PaymentLineItem {
  name: string;
  details: string[];
  quantity: number;
  unitPrice: number;
  amount: number;
}

/**
 * The priced line for the document. A one-time booking is charged per
 * vehicle (package price x vehicles), so quantity is the vehicle count and the
 * unit price is derived from the stored total -- that keeps the line in step
 * with the amount actually billed even if the package price changed since.
 */
export function buildPaymentLineItem(
  payment: PaymentOutput,
  vehicles: Pick<Vehicle, "make" | "model" | "plateNumber">[],
): PaymentLineItem {
  const vehicleLabels = vehicles.map(formatVehicleLabel);

  if (payment.serviceType === "subscription") {
    return {
      name: payment.serviceName?.trim() || "Subscription Plan",
      details: ["Subscription plan", ...vehicleLabels],
      quantity: 1,
      unitPrice: payment.totalAmount,
      amount: payment.totalAmount,
    };
  }

  const quantity = Math.max(1, vehicles.length);
  return {
    name: payment.serviceName?.trim() || "Car Wash Service",
    details: vehicleLabels,
    quantity,
    unitPrice: payment.totalAmount / quantity,
    amount: payment.totalAmount,
  };
}
